import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import CardHeader from "@material-ui/core/CardHeader";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { Pagination, Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    padding: 0,
    borderBottom: `0px solid ${theme.palette.divider}`,
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    overflowY: "auto",
  },
  listItem: {
    display: "flex",
    padding: 0,
  },
}));

const LoadingItem = () => (
  <ListItem style={{ padding: ".5rem" }}>
    <Skeleton />
  </ListItem>
);

function getFilteredStores(stores, filter) {
  if (!filter) return stores;
  const value = filter.toLowerCase();
  return stores.filter(
    (store) =>
      store.Nome?.toLowerCase().includes(value) ||
      store.Apelido?.toLowerCase().includes(value)
  );
}

export default function StoreList({
  stores = [],
  selectedStoreIds = [],
  onSelectedChange,
  loading = false,
  label = "Lojas",
  rowsPerPage = 5,
}) {
  const classes = useStyles();
  const [filter, setFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [filter]);

  const filteredStores = getFilteredStores(stores, filter);
  const totalPages = Math.ceil(filteredStores.length / rowsPerPage);
  const paginatedStores = filteredStores.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const allSelected =
    selectedStoreIds.length === filteredStores.length &&
    filteredStores.length > 0;
  const allPageSelected =
    paginatedStores.length > 0 &&
    paginatedStores.every((store) => selectedStoreIds.includes(store.IdLoja));
  const somePageSelected = paginatedStores.some((store) =>
    selectedStoreIds.includes(store.IdLoja)
  );

  const handleToggleAll = () => {
    if (!somePageSelected) {
      const newSelected = [
        ...selectedStoreIds,
        ...paginatedStores
          .map((s) => s.IdLoja)
          .filter((id) => !selectedStoreIds.includes(id)),
      ];
      onSelectedChange(newSelected);
    } else if (allPageSelected && !allSelected) {
      onSelectedChange(filteredStores.map((s) => s.IdLoja));
    } else if (allSelected) {
      onSelectedChange([]);
    } else {
      const newSelected = [
        ...selectedStoreIds,
        ...paginatedStores
          .map((s) => s.IdLoja)
          .filter((id) => !selectedStoreIds.includes(id)),
      ];
      onSelectedChange(newSelected);
    }
  };

  const handleToggleStore = (storeId) => {
    if (selectedStoreIds.includes(storeId)) {
      onSelectedChange(selectedStoreIds.filter((id) => id !== storeId));
    } else {
      onSelectedChange([...selectedStoreIds, storeId]);
    }
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handlePageClick = (event, value) => {
    setCurrentPage(value - 1);
  };

  let selectAllTooltip = "";
  if (!somePageSelected) {
    selectAllTooltip = "Selecionar todas as lojas desta página";
  } else if (allPageSelected && !allSelected) {
    selectAllTooltip = "Selecionar todas as lojas";
  } else if (allSelected) {
    selectAllTooltip = "Desmarcar todas as lojas";
  } else {
    selectAllTooltip = "Selecionar todas as lojas desta página";
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Tooltip title={selectAllTooltip} arrow>
              <Checkbox
                onClick={handleToggleAll}
                checked={allSelected}
                indeterminate={!allSelected && somePageSelected}
                disabled={paginatedStores.length === 0}
                color="primary"
              />
            </Tooltip>
          }
          title={label}
          titleTypographyProps={{ color: "primary" }}
          subheader={`${selectedStoreIds.length}/${filteredStores.length} selecionadas`}
          subheaderTypographyProps={{ color: "textPrimary" }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label={`Filtrar ${label}`}
          type="text"
          fullWidth
          variant="outlined"
          disabled={loading}
          value={filter}
          onChange={handleFilterChange}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.list}>
          {loading ? (
            <>
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
            </>
          ) : (
            paginatedStores.map((store, index) => {
              const labelId = `store-list-item-${index}`;
              return (
                <ListItem
                  key={store.IdLoja}
                  role="listitem"
                  onClick={() => handleToggleStore(store.IdLoja)}
                  className={classes.listItem}
                  button
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedStoreIds.includes(store.IdLoja)}
                      tabIndex={-1}
                      inputProps={{ "aria-labelledby": labelId }}
                      color="primary"
                    />
                  </ListItemIcon>
                  <Tooltip title={store.Apelido || ""}>
                    <ListItemText id={labelId} primary={store.Nome} />
                  </Tooltip>
                </ListItem>
              );
            })
          )}
        </div>
      </Grid>

      <Grid item xs={12}>
        <Pagination
          count={totalPages}
          page={currentPage + 1}
          onChange={handlePageClick}
          color="primary"
          size="small"
          variant="text"
          style={{ margin: "0 auto" }}
        />
      </Grid>
    </Grid>
  );
}
